import React from "react";
import { Link, navigate } from "gatsby";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { Box, Flex, Text } from "theme-ui";
import Scrollspy from "react-scrollspy";
import { Button, Icon } from "../atoms";
import { colors, typography } from "../../styles/theme";

const StickyBottomMenu = ({
  variant,
  items,
  ctaText,
  ctaButtonText,
  ctaLink,
  ctaButtonId,
  customStyles,
}) => {
  if (variant === "cta") {
    return (
      <>
        {/* Desktop/Tablet View */}
        <Box sx={{ ...ctaStyles.containerDesktop, ...customStyles }}>
          <Flex sx={ctaStyles.innerDesktop}>
            <Text sx={ctaStyles.textDesktop}>
              {ctaText}
              <Flex as="span" sx={ctaStyles.arrow}>
                →
              </Flex>
            </Text>
            <Button
              id={ctaButtonId}
              variant="filled"
              onClick={() => navigate(ctaLink)}
            >
              {ctaButtonText}
            </Button>
          </Flex>
        </Box>

        {/* Mobile View */}
        <Box sx={{ ...ctaStyles.containerMobile, ...customStyles }}>
          <Text sx={ctaStyles.textMobile}>{ctaText}</Text>
          <Button
            id={ctaButtonId}
            variant="filled"
            size="large"
            onClick={() => navigate(ctaLink)}
            customStyles={ctaStyles.buttonMobile}
          >
            {ctaButtonText}
          </Button>
        </Box>
      </>
    );
  }

  // Default Variant (scrollspy menu)
  const itemLinks = items?.map((i) => i.hash) || [];

  return (
    <Box sx={{ ...styles.container, ...customStyles }}>
      <StyledScrollSpy
        items={itemLinks}
        currentClassName="is-current"
        style={{ ...typography.textMd500 }}
        offset={-10}
      >
        {items?.map(({ text, icon, hash, id, cta }, i) => (
          <li key={`sticky-menu-item-${i}`}>
            {cta ? (
              <Flex sx={{ alignItems: "center", gap: "5.938rem" }}>
                <Text sx={styles.text}>
                  {text}{" "}
                  <Flex as="span" sx={styles.arrow}>
                    →
                  </Flex>
                </Text>
                <Button
                  id={cta.id}
                  variant={cta.variant || "filled"}
                  iconName={cta.icon}
                  direction="ltr"
                  onClick={() => navigate(cta.href)}
                >
                  {cta.text}
                </Button>
              </Flex>
            ) : (
              <Link to={`#${hash}`} key={`item-${i}`} id={id}>
                <Icon name={icon} pointerCursor />
                <Text sx={{ ...typography.textMd500 }}>{text}</Text>
              </Link>
            )}
          </li>
        ))}
      </StyledScrollSpy>
    </Box>
  );
};

const styles = {
  container: {
    display: "block",
    p: "1.5rem 7.5rem",
    borderRadius: "99999px",
    width: "max-content",
    position: "fixed",
    bottom: "1.5rem",
    left: "50%",
    transform: "translate(-50%, 0)",
    mx: "auto",
    zIndex: 98,
    background:
      "linear-gradient(0deg,rgba(255, 255, 255, 0.2) 0%,rgba(255, 255, 255, 0.2) 100%),rgba(237, 237, 237, 0.2)",
    backdropFilter: "blur(20px)",
    border: `1.14px solid ${colors.neutral200}`,
    "@media(max-width: 1203px)": {
      display: "none",
    },
  },
  text: {
    ...typography.textMd500,
    alignItems: "center",
    gap: "0.5rem",
    display: "flex",
  },
  arrow: {
    width: "1.5rem",
    height: "1.5rem",
    fontSize: "1.5rem",
    lineHeight: "1.5rem",
    alignItems: "center",
    justifyContent: "center",
  },
};

const ctaStyles = {
  containerDesktop: {
    display: ["none", "none", "none", "block"],
    position: "fixed",
    bottom: "1.5rem",
    left: "50%",
    transform: "translate(-50%, 0)",
    zIndex: 98,
    background:
      "linear-gradient(0deg,rgba(255, 255, 255, 0.2) 0%,rgba(255, 255, 255, 0.2) 100%),rgba(237, 237, 237, 0.2)",
    backdropFilter: "blur(20px)",
    border: `1.14px solid ${colors.neutral200}`,
    borderRadius: "99999px",
    py: "1rem",
    px: "4rem",
    whiteSpace: "nowrap",
    "@media(min-width: 1280px)": {
      minWidth: "max-content",
      flexShrink: 0,
    },
  },
  innerDesktop: {
    alignItems: "center",
    gap: "4rem",
  },
  textDesktop: {
    ...typography.textMd500,
    color: colors.black,
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  arrow: {
    fontSize: "1.25rem",
    lineHeight: "1.25rem",
    alignItems: "center",
    justifyContent: "center",
  },
  containerMobile: {
    display: ["block", "block", "block", "none"],
    position: "fixed",
    bottom: "1rem",
    left: "50%",
    transform: "translateX(-50%)",
    width: "calc(100% - 2rem)",
    maxWidth: "28.75rem",
    zIndex: 98,
    background:
      "linear-gradient(0deg,rgba(255, 255, 255, 0.2) 0%,rgba(255, 255, 255, 0.2) 100%),rgba(237, 237, 237, 0.2)",
    backdropFilter: "blur(20px)",
    border: `1px solid ${colors.neutral200}`,
    borderRadius: "1.25rem",
    py: "1rem",
    px: "2rem",
    textAlign: "center",
  },
  textMobile: {
    ...typography.textMd500,
    color: colors.black,
    display: "block",
    mb: "1.5rem",
  },
  buttonMobile: {
    justifyContent: "center",
  },
};

const StyledScrollSpy = styled(Scrollspy)`
  padding: 0;
  margin: 0;
  display: flex;
  gap: 3rem;
  & > li {
    list-style-type: none;
    padding: 0;
  }

  & > li > a {
    color: ${colors.black};
    text-decoration: none;
    transition: 1s all ease-in-out;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    svg {
      stroke: ${colors.black};
    }
  }

  & > li > a:hover {
    span {
      color: ${colors.primaryMain};
    }
    svg {
      stroke: ${colors.primaryMain};
    }
  }

  & > li.is-current > a {
    span {
      color: ${colors.primaryMain};
    }
    svg {
      stroke: ${colors.primaryMain};
    }
  }
`;

StickyBottomMenu.propTypes = {
  variant: PropTypes.oneOf(["default", "cta"]),
  items: PropTypes.array,
  ctaText: PropTypes.string,
  ctaButtonText: PropTypes.string,
  ctaLink: PropTypes.string,
  ctaButtonId: PropTypes.string,
  customStyles: PropTypes.object,
};

StickyBottomMenu.defaultProps = {
  variant: "default",
};

export default StickyBottomMenu;
